import React from 'react'
import { useSelector } from 'react-redux'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { BulletedList } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bulletedList/BulletedList'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ContentContainer } from '@lyrahealth-inc/ui-core-crossplatform/src/templates/content/ContentContainer'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

import {
  SchedulingFooterInternationalConnected,
  SchedulingFooterInternationalNotConnected,
  SchedulingFooterSupportConnected,
  SchedulingFooterSupportNotConnected,
} from './constants'
import { actions, mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import { getAuthConfig } from '../data/auth/authSelectors'

const DescriptionContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['24px'],
}))

const TitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
  marginTop: theme.spacing['32px'],
}))

export const SchedulingFooter: React.FC<SchedulingFooterProps> = ({
  providerCalendarState,
  schedulingCredentialsValid,
}) => {
  const { colors } = useTheme()
  const config = useSelector(getAuthConfig)
  const supportConstants =
    providerCalendarState === 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE' || schedulingCredentialsValid
      ? SchedulingFooterSupportConnected
      : SchedulingFooterSupportNotConnected
  const internationalSupportConstants =
    providerCalendarState === 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE' || schedulingCredentialsValid
      ? SchedulingFooterInternationalConnected
      : SchedulingFooterInternationalNotConnected

  const ticketSubmission = () => {
    track({ event: mixpanelEvents.BUTTON_PRESS, action: actions.SCHEDULING_FOOTER_SUBMIT_TICKET })
    window.open('https://provider-support.lyrahealth.com/hc/en-us/requests/new?ticket_form_id=26162178922387', '_blank')
  }
  const internationalTicketSubmission = () => {
    track({ event: mixpanelEvents.BUTTON_PRESS, action: actions.SCHEDULING_INTERNATIONAL_FOOTER_SUBMIT_TICKET })
    window.open('https://icas-provider-support.zendesk.com/hc/en-us/requests/new', '_blank')
  }

  const generateSupport = (supportElement: { text: string; link: string; mixpanelAction: string }) => {
    return {
      value: (
        <Link
          text={supportElement.text}
          onPress={() => {
            track({ event: mixpanelEvents.BUTTON_PRESS, action: supportElement.mixpanelAction })
            window.open(supportElement.link, '_blank')
          }}
          size={BodyTextSize.DEFAULT}
        />
      ),
    }
  }

  const supportListData = config?.internationalSmartSchedulingEnabled
    ? internationalSupportConstants.map((supportElement) => generateSupport(supportElement))
    : supportConstants.map((supportElement) => generateSupport(supportElement))

  return (
    <ContentContainer>
      <TitleContainer>
        <Title level='3' color={colors.textSecondary} size={TitleSize.MEDIUM} text='Support' />
      </TitleContainer>
      <BulletedList
        spaceBetween={0}
        textColor={colors.linkDefault}
        bulletColor={colors.linkDefault}
        data={supportListData}
      />
      <TitleContainer>
        <Title level='3' size={TitleSize.MEDIUM} color={colors.textSecondary} text='Still Need Help?' />
      </TitleContainer>
      <DescriptionContainer>
        <BodyText
          color={colors.textSecondary}
          text={
            <>
              Submit a ticket{' '}
              <Link
                text='here'
                onPress={config?.internationalSmartSchedulingEnabled ? internationalTicketSubmission : ticketSubmission}
                size={BodyTextSize.DEFAULT}
              />
              . We’ll get back to you soon.
            </>
          }
        />
      </DescriptionContainer>
    </ContentContainer>
  )
}

type SchedulingFooterProps = {
  providerCalendarState: string
  schedulingCredentialsValid: boolean
}
